import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import type { User } from '../../types';

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  selectedOrgId?: string;
  selectedOrgName?: string;
  onRemoveFromOrg: () => void;
  onDeleteEntirely: () => void;
  isRemovingFromOrg: boolean;
  isDeletingEntirely: boolean;
  canDeleteEntirely: boolean;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  selectedOrgId,
  selectedOrgName,
  onRemoveFromOrg,
  onDeleteEntirely,
  isRemovingFromOrg,
  isDeletingEntirely,
  canDeleteEntirely,
}: DeleteUserDialogProps) {
  if (!user) return null;

  const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email;
  const hasMultipleOrgs = (user.organisations?.length || 0) > 1;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete User
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-3">
            <p>
              You are about to delete <strong>{fullName}</strong> ({user.email}).
            </p>
            {selectedOrgId && selectedOrgName && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Remove from Account</p>
                <p className="text-sm text-muted-foreground">
                  Remove this user from <strong>{selectedOrgName}</strong> only.
                  {hasMultipleOrgs && ' They will remain in other accounts.'}
                </p>
              </div>
            )}
            {canDeleteEntirely && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-700">Delete Entirely</p>
                <p className="text-sm text-red-600">
                  Permanently delete this user from all accounts. This action cannot be undone.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel disabled={isRemovingFromOrg || isDeletingEntirely}>Cancel</AlertDialogCancel>
          {selectedOrgId && (
            <Button
              variant="outline"
              onClick={onRemoveFromOrg}
              disabled={isRemovingFromOrg || isDeletingEntirely}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              {isRemovingFromOrg ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove from Account'
              )}
            </Button>
          )}
          {canDeleteEntirely && (
            <AlertDialogAction
              onClick={onDeleteEntirely}
              disabled={isRemovingFromOrg || isDeletingEntirely}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingEntirely ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Entirely'
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
